import React, { useState, useEffect, useRef } from 'react';
import { Input } from './ui/input';
import { Loader2, X } from 'lucide-react';
import api from '../lib/api';

export const UserPicker = ({ value, onChange, placeholder = 'Search employees...', 'data-testid': testId }) => {
  const [query, setQuery] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [recentUsers, setRecentUsers] = useState([]);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  // Load all active users and recent assignments once
  useEffect(() => {
    const load = async () => {
      try {
        const [uRes, rRes] = await Promise.all([
          api.get('/smb/users'),
          api.get('/smb/users/recently-assigned?limit=5'),
        ]);
        if (uRes.data.success) setAllUsers(uRes.data.data);
        if (rRes.data.success) setRecentUsers(rRes.data.data);
      } catch {}
    };
    load();
  }, []);

  // Resolve display name from value (user ID)
  useEffect(() => {
    if (!value) { setDisplayName(''); return; }
    const user = allUsers.find(u => u.id === value);
    if (user) setDisplayName(`${user.first_name} ${user.last_name}`);
  }, [value, allUsers]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleFocus = () => {
    // Show recently assigned on focus
    if (recentUsers.length > 0) {
      setOptions(recentUsers);
      setOpen(true);
    } else if (allUsers.length > 0) {
      setOptions(allUsers.slice(0, 5));
      setOpen(true);
    }
  };

  const handleInput = (val) => {
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!val.trim()) {
      setOptions(recentUsers.length > 0 ? recentUsers : allUsers.slice(0, 5));
      setOpen(true);
      return;
    }
    debounceRef.current = setTimeout(() => {
      const q = val.toLowerCase();
      const filtered = allUsers.filter(u =>
        `${u.first_name} ${u.last_name}`.toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q)
      );
      setOptions(filtered);
      setOpen(filtered.length > 0);
    }, 150);
  };

  const handleSelect = (user) => {
    onChange(user.id);
    setDisplayName(`${user.first_name} ${user.last_name}`);
    setQuery('');
    setOpen(false);
  };

  const handleClear = () => {
    onChange('');
    setDisplayName('');
    setQuery('');
  };

  const isSearching = open || query;

  return (
    <div ref={wrapperRef} className="relative">
      {isSearching ? (
        <Input
          value={query}
          onChange={e => handleInput(e.target.value)}
          onFocus={handleFocus}
          placeholder={placeholder}
          autoFocus
          data-testid={testId}
        />
      ) : (
        <button
          type="button"
          onClick={handleFocus}
          className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors hover:bg-muted/50 focus:outline-none focus:ring-1 focus:ring-ring"
          data-testid={testId}
        >
          <span className={displayName ? 'text-foreground' : 'text-muted-foreground'}>
            {displayName || placeholder}
          </span>
          {value && (
            <X className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" onClick={(e) => { e.stopPropagation(); handleClear(); }} />
          )}
        </button>
      )}
      {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />}
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-popover shadow-lg max-h-48 overflow-auto" data-testid={`${testId}-dropdown`}>
          {!query && recentUsers.length > 0 && (
            <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Recently Assigned</div>
          )}
          {options.map((user) => (
            <button
              key={user.id}
              type="button"
              className={`w-full text-left px-3 py-2 text-sm hover:bg-muted/50 transition-colors ${user.id === value ? 'bg-muted/30 font-medium' : ''}`}
              onClick={() => handleSelect(user)}
              data-testid={`${testId}-option-${user.id}`}
            >
              {user.first_name} {user.last_name}
              <span className="text-xs text-muted-foreground ml-2">{user.email}</span>
            </button>
          ))}
          {options.length === 0 && query && (
            <div className="px-3 py-2 text-sm text-muted-foreground">No employees found</div>
          )}
        </div>
      )}
    </div>
  );
};
